import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PhotoCarousel } from "@/components/PhotoCarousel";
import { ImageUploader } from "@/components/ImageUploader";
import { PropertyEditDialog } from "@/components/landlord/PropertyEditDialog";
import { ImagePlus, Pencil } from "lucide-react";

export function PostedPropertyCard({ p, onViewMatches, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [images, setImages] = useState(p.images || []);
  const [saving, setSaving] = useState(false);

  const save = async (next) => {
    setImages(next);
    setSaving(true);
    try {
      await api.patch(`/properties/${p.id}/images`, { images: next });
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update photos");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-border rounded-sm overflow-hidden" data-testid={`posted-property-${p.id}`}>
      <PhotoCarousel images={images} alt={p.title} heightClass="h-36" testid={`prop-carousel-${p.id}`} />
      <div className="p-4 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold truncate">{p.title}</div>
          <div className="text-xs text-muted-foreground truncate">{p.address}, {p.city} · ${p.rent}/mo</div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button size="sm" variant="ghost" data-testid={`edit-listing-${p.id}`} onClick={() => setEditOpen(true)}>
            <Pencil className="w-4 h-4 mr-1" /> Edit
          </Button>
          <Button size="sm" variant="ghost" data-testid={`edit-photos-${p.id}`} onClick={() => setEditing((v) => !v)}>
            <ImagePlus className="w-4 h-4 mr-1" /> {editing ? "Done" : "Photos"}
          </Button>
          <Button size="sm" variant="outline" data-testid={`matches-${p.id}`} onClick={() => onViewMatches(p)}>Matches</Button>
        </div>
      </div>
      {editing && (
        <div className="px-4 pb-4 pt-1 border-t border-border">
          <ImageUploader value={images} onChange={save} multiple featured testid={`edit-photos-uploader-${p.id}`} />
          {saving && <div className="text-xs text-muted-foreground mt-1">Saving…</div>}
        </div>
      )}
      {editOpen && <PropertyEditDialog property={p} open={editOpen} onClose={() => setEditOpen(false)} onSaved={onChanged} />}
    </div>
  );
}
